import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, BookOpen, MessageSquare, Terminal, GitBranch, Sparkles } from "lucide-react";
import { HostingMeter } from "@/components/HostingMeter";

const tiles = [
  { to: "/refinery", icon: Zap, title: "REFINERY", body: "Type your rough goal. Get a production-grade prompt tuned to the exact model you're using.", testid: "home-tile-refinery" },
  { to: "/models", icon: GitBranch, title: "MODELS", body: "21 AIs, 21 personalities. Learn each one's quirks, XML tags, weighting syntax, and best-fit prompt patterns.", testid: "home-tile-models" },
  { to: "/library", icon: BookOpen, title: "LIBRARY", body: "Curated prompts for anatomy studies, safety info, deep research, photoshop, fact-checking — copy-paste ready.", testid: "home-tile-library" },
  { to: "/coach", icon: MessageSquare, title: "COACH", body: "Socratic dialogue that teaches you to write prompts in your own voice. No copy-paste — just skill.", testid: "home-tile-coach" },
  { to: "/techniques", icon: Terminal, title: "TECHNIQUES", body: "Chain-of-thought, adversarial self-check, factual grounding, anti-propaganda framing.", testid: "home-tile-techniques" },
  { to: "/recap", icon: ShieldCheck, title: "SESSION RECAP", body: "End a session with a resume-prompt you paste into any AI next time to pick up exactly where you left off.", testid: "home-tile-recap" },
  { to: "/adlab", icon: Sparkles, title: "AD LAB", body: "Generates funny, original promo scripts. AI-vs-human comedy. You post them; the site drafts them.", testid: "home-tile-adlab" },
];

export const Home = ({ onDonate }) => {
  return (
    <div data-testid="home-page" className="grain">
      {/* Hero */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32 relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-6">
              <span className="w-2 h-2 bg-[color:var(--accent-matrix)] rounded-full animate-pulse" />
              <span data-testid="hero-eyebrow">SAIMONSAYS // v0.1.0 // truth-mode default: ON</span>
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tighter max-w-5xl">
              write prompts that make<br />
              <span className="text-[color:var(--accent-amber)]">machines tell the truth</span>
              <span className="cursor" />
            </h1>
            <p className="mt-8 max-w-2xl text-neutral-400 text-base sm:text-lg leading-relaxed">
              A field manual for talking to AI. Model-specific tactics, curated prompts, a live refinery,
              and a coach that teaches you the craft in your own voice. Zero propaganda. Zero hedging.
              Just clean, cited, useful output.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/refinery"
                data-testid="hero-cta-refinery"
                className="inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white duration-150"
                style={{ transitionProperty: "background-color" }}
              >
                open refinery <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/techniques"
                data-testid="hero-cta-techniques"
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs uppercase tracking-widest hover:border-[color:var(--accent-amber)] hover:text-[color:var(--accent-amber)] duration-150"
                style={{ transitionProperty: "color, border-color" }}
              >
                learn techniques
              </Link>
            </div>
          </div>

          {/* Whoracle — the mascot, greeting every visitor */}
          <div className="relative group order-first lg:order-last mx-auto lg:mx-0" data-testid="hero-whoracle">
            <div className="whoracle-glow absolute -inset-8 bg-[color:var(--accent-matrix)]/15 blur-3xl pointer-events-none" />
            <img
              src="/social/generated/whoracle-logo-v4.png"
              alt="Whoracle — sAimonsays oracle mascot"
              className="whoracle-float relative w-80 sm:w-[28rem] lg:w-[36rem] xl:w-[42rem] h-auto object-contain drop-shadow-[0_0_40px_rgba(0,255,120,0.25)] group-hover:drop-shadow-[0_0_70px_rgba(0,255,120,0.5)] duration-500"
              style={{ transitionProperty: "filter" }}
              loading="eager"
            />
            <div className="mt-2 text-center text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)]/70">
              // whoracle · your oracle
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="border-b border-white/10 bg-black">
        <div className="mx-auto max-w-7xl px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-0">
          {[
            ["01", "TRUTH-BIASED", "Every generated prompt forces citation, uncertainty admission, and multi-source framing."],
            ["02", "MODEL-AWARE", "The same prompt fails on Midjourney and shines on Claude. We know the difference."],
            ["03", "SKILL-FIRST", "We'd rather teach you to prompt than hand you a template. Both are here."],
          ].map(([n, t, b]) => (
            <div key={n} className="p-6 md:border-r md:last:border-r-0 border-white/10">
              <div className="text-[10px] tracking-[0.3em] text-[color:var(--accent-amber)]">// {n}</div>
              <div className="font-display font-bold mt-3 text-lg">{t}</div>
              <div className="text-sm text-neutral-400 mt-2 leading-relaxed">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Tiles */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="font-display font-bold text-2xl">// modules</h2>
          <div className="text-xs text-neutral-500 uppercase tracking-widest">6 tools</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {tiles.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="group bg-[#0a0a0a] hover:bg-[#141414] p-8 flex flex-col gap-3 relative overflow-hidden"
              style={{ transitionProperty: "background-color", transitionDuration: "150ms" }}
            >
              <t.icon className="w-5 h-5 text-neutral-500 group-hover:text-[color:var(--accent-amber)]" />
              <div className="font-display font-bold text-base tracking-tight">{t.title}</div>
              <div className="text-sm text-neutral-400 leading-relaxed">{t.body}</div>
              <div className="mt-auto pt-4 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--accent-amber)] opacity-0 group-hover:opacity-100 duration-150"
                   style={{ transitionProperty: "opacity" }}>
                open <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <HostingMeter onDonate={onDonate} />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-8 text-xs text-neutral-500 flex flex-wrap gap-3 justify-between border-t border-white/5">
          <span>SAIMONSAYS.AI // built for humans who want machines to tell the truth</span>
          <span>21 models // 7 techniques // 21 curated prompts</span>
          <span>{new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
};
